import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { readErr } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PlaceAutocomplete from '@/components/PlaceAutocomplete';
import ProfilesSection from '@/components/ProfilesSection';
import { toast } from '@/lib/toast';
import { fetchRegion, formatPrice } from '@/lib/region';
import { User, Coins, MapPin, Lock, Loader2, Check, Plus, ArrowLeft } from 'lucide-react';

export default function Profile() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState(user?.name || '');
    const [bd, setBd] = useState(user?.birth_details || { date: '', time: '', place: '' });
    const [placeText, setPlaceText] = useState(user?.birth_details?.place || '');
    const [resolved, setResolved] = useState(user?.birth_details || null);
    const [saving, setSaving] = useState(false);
    const [pw, setPw] = useState({ current_password: '', new_password: '' });
    const [consultations, setConsultations] = useState([]);
    // Live wallet balance — fetched on mount so the Profile card never
    // shows a stale figure when the cached `user.wallet_balance_inr`
    // hasn't been refreshed since a chat tick / recharge / BTR charge.
    const [walletInr, setWalletInr] = useState(null);
    // Round 40n — currency-aware wallet display on the profile card.
    const [currency, setCurrency] = useState('INR');

    useEffect(() => {
        api.get('/consultations/mine').then((r) => setConsultations(r.data)).catch(() => {});
        api.get('/wallet/balance')
            .then((r) => setWalletInr(Number(r.data?.balance_inr ?? 0)))
            .catch(() => setWalletInr(Number(user?.wallet_balance_inr ?? 0)));
        fetchRegion()
            .then((r) => setCurrency(r?.currency || 'INR'))
            .catch(() => { /* default INR */ });
        // Also refresh the auth context so the sidebar/nav balance stays in sync.
        refresh().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveProfile = async () => {
        setSaving(true);
        try {
            // Resolve place if user typed something different
            let coords = resolved;
            if (placeText && (!coords || coords.place !== placeText)) {
                try {
                    const { data } = await api.post('/astrology/geocode', { place: placeText });
                    coords = data;
                } catch {
                    toast.error('Could not resolve place — pick from the dropdown');
                    setSaving(false);
                    return;
                }
            }
            const { data: saved } = await api.put('/auth/me', {
                name,
                birth_details: coords ? {
                    date: bd.date, time: bd.time, place: coords.place,
                    lat: coords.lat, lon: coords.lon, tz: coords.tz,
                } : bd,
            });
            if (coords) {
                setResolved(coords);
                setPlaceText(coords.place);
                setBd({ ...bd, place: coords.place });
            }
            await refresh();
            if (saved?.duplicate_warning) {
                toast.error(saved.duplicate_warning.message, { duration: 9000 });
            } else {
                toast.success('Saved');
            }
        } catch (err) {
            toast.error(readErr(err, 'Save failed'));
        } finally { setSaving(false); }
    };

    const changePw = async (e) => {
        e.preventDefault();
        try {
            await api.post('/auth/change-password', pw);
            toast.success('Password changed');
            setPw({ current_password: '', new_password: '' });
        } catch (err) {
            toast.error(readErr(err, 'Change failed'));
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-6 py-10 md:py-16 space-y-10" data-testid="profile-page">
            <button
                onClick={() => navigate('/app')}
                data-testid="profile-back-btn"
                className="text-sm text-slate-400 hover:text-marigold inline-flex items-center gap-1 -mb-4 self-start"
            >
                <ArrowLeft size={14} /> Back to dashboard
            </button>

            <div>
                <div className="overline mb-3">Account</div>
                <h1 className="font-serif text-4xl md:text-5xl text-parchment">Profile</h1>
            </div>

            <Card className="bg-elevated border-marigold/15 rounded-sm p-8" data-testid="profile-card">
                <div className="flex items-center gap-3 mb-6">
                    <User className="text-marigold" size={20} />
                    <h2 className="font-serif text-2xl text-parchment">Identity</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Name" value={name} onChange={setName} testId="profile-name" />
                    <Field label="Email" value={user?.email || ''} readOnly testId="profile-email" />
                </div>
            </Card>

            <Card className="bg-elevated border-marigold/15 rounded-sm p-8" data-testid="birth-card">
                <div className="flex items-center gap-3 mb-6">
                    <MapPin className="text-marigold" size={20} />
                    <h2 className="font-serif text-2xl text-parchment">Birth details</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                        <Label className="text-slate-300 text-xs uppercase tracking-wider">Date</Label>
                        <Input type="date" value={bd.date || ''} onChange={(e) => setBd({ ...bd, date: e.target.value })}
                            className="mt-2 bg-surface border-marigold/15 rounded-sm text-parchment h-11"
                            data-testid="profile-birth-date" />
                    </div>
                    <div>
                        <Label className="text-slate-300 text-xs uppercase tracking-wider">Time</Label>
                        <Input type="time" value={bd.time || ''} onChange={(e) => setBd({ ...bd, time: e.target.value })}
                            className="mt-2 bg-surface border-marigold/15 rounded-sm text-parchment h-11"
                            data-testid="profile-birth-time" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-slate-300 text-xs uppercase tracking-wider">Place</Label>
                        <div className="mt-2">
                            <PlaceAutocomplete
                                value={placeText}
                                onTextChange={setPlaceText}
                                onSelect={(loc) => { setResolved(loc); setPlaceText(loc.place); setBd({ ...bd, place: loc.place }); }}
                                testId="profile-birth-place"
                            />
                        </div>
                        {resolved && resolved.place === placeText && (
                            <div className="mt-2 text-xs text-marigold/90">
                                {resolved.lat}, {resolved.lon} · {resolved.tz}
                            </div>
                        )}
                    </div>
                </div>
                <Button onClick={saveProfile} disabled={saving}
                    data-testid="save-profile"
                    className="mt-6 bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11 px-6">
                    {saving ? 'Saving…' : 'Save changes'}
                </Button>
            </Card>

            <ProfilesSection />

            <BirthTimeAnalysisCard />

            <Card className="bg-elevated border-marigold/15 rounded-sm p-8" data-testid="pw-card">
                <div className="flex items-center gap-3 mb-6">
                    <Lock className="text-marigold" size={20} />
                    <h2 className="font-serif text-2xl text-parchment">Change password</h2>
                </div>
                <form onSubmit={changePw} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Current password" type="password"
                            value={pw.current_password} onChange={(v) => setPw({ ...pw, current_password: v })}
                            testId="current-password" />
                        <Field label="New password" type="password"
                            value={pw.new_password} onChange={(v) => setPw({ ...pw, new_password: v })}
                            testId="new-password" />
                    </div>
                    <Button type="submit"
                        data-testid="change-password-submit"
                        className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11 px-6">
                        Update password
                    </Button>
                </form>
            </Card>

            <Card className="bg-surface border-marigold/15 rounded-sm p-8" data-testid="wallet-summary">
                <div className="flex items-center gap-3 mb-6">
                    <Coins className="text-marigold" size={20} />
                    <h2 className="font-serif text-2xl text-parchment">Wallet &amp; bookings</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                        <div className="overline">Wallet balance</div>
                        <div className="mt-2 flex items-end justify-between gap-3 flex-wrap">
                            <div className="font-serif text-4xl text-parchment leading-none" data-testid="wallet-balance-inr">
                                {formatPrice(Number(walletInr ?? user?.wallet_balance_inr ?? 0), currency)}
                            </div>
                            <Link to="/app/chat" data-testid="topup-wallet">
                                <Button
                                    size="sm"
                                    className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-sm h-9 px-4 text-xs uppercase tracking-widest"
                                >
                                    <Plus size={14} className="mr-1.5" /> Top up
                                </Button>
                            </Link>
                        </div>
                        <div className="text-xs text-slate-500 mt-2">
                            {currency === 'USD' ? '$1' : '₹25'} / minute of live chat with Guruji · wallet balance never expires
                        </div>
                    </div>
                    <div>
                        <div className="overline">Consultations booked</div>
                        <div className="font-serif text-4xl text-parchment mt-2">{consultations.length}</div>
                    </div>
                </div>
                {consultations.length > 0 && (
                    <ul className="mt-6 space-y-2 text-sm" data-testid="consultations-list">
                        {consultations.slice(0, 5).map((c) => (
                            <li key={c.id} className="flex items-center justify-between py-2 border-t border-marigold/5">
                                <span className="text-parchment">{c.expert_name} · {c.duration_min} min</span>
                                <span className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </div>
    );
}

function Field({ label, value, onChange, type = 'text', readOnly, testId }) {
    return (
        <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
            <Input type={type} value={value}
                onChange={(e) => onChange?.(e.target.value)}
                readOnly={readOnly}
                data-testid={testId}
                className="mt-2 bg-surface border-marigold/15 rounded-sm text-parchment h-11 disabled:opacity-50" />
        </div>
    );
}


function BirthTimeAnalysisCard() {
    const { user } = useAuth();
    const [expanded, setExpanded] = useState(false);
    const conf = user?.birth_time_confidence;
    if (conf !== 'rectified') return null;

    const result = user?.birth_time_rectification_result || {};
    const rectifiedAt = user?.birth_time_rectified_at;
    const rectifiedDate = rectifiedAt
        ? new Date(rectifiedAt).toLocaleDateString(undefined,
            { year: 'numeric', month: 'long', day: 'numeric' })
        : '—';
    const time = result.rectified_time_local || user?.birth_details?.time || '—';
    const lagna = result.lagna_sign;
    const lagnaDeg = result.lagna_degree;
    const chain = [result.sign_lord, result.star_lord, result.sub_lord, result.sub_sub_lord]
        .filter(Boolean).join(' · ');
    const window = result.window_minutes_used;
    const confidenceUpper = (result.confidence || '').toUpperCase();

    return (
        <Card className="bg-elevated border-marigold/15 rounded-sm p-8" data-testid="rectification-history-card">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center justify-between text-left"
                data-testid="rectification-toggle"
            >
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <h2 className="font-serif text-2xl text-parchment">Birth-time analysis</h2>
                        <span className="text-marigold text-sm">✓</span>
                    </div>
                    <p className="text-sm text-slate-400">
                        Analysed on {rectifiedDate} · <span className="text-marigold">{time}</span>
                    </p>
                </div>
                <span className="text-slate-500 text-sm">{expanded ? '−' : '+'}</span>
            </button>
            {expanded && (
                <div className="mt-6 space-y-3 text-sm text-slate-300 border-t border-marigold/10 pt-6"
                     data-testid="rectification-detail">
                    <p>
                        On <span className="text-parchment">{rectifiedDate}</span>, the KP Ruling Planets
                        method was applied to your birth details.
                    </p>
                    {window != null && (
                        <p>
                            Your birth window of <span className="text-parchment">±{window} minutes</span>
                            {' '}around your stated time was analysed.
                        </p>
                    )}
                    <p>
                        The analysis identified <span className="text-marigold font-medium">{time}</span>
                        {' '}as your precise birth time.
                    </p>
                    {lagna && (
                        <p>
                            Ascendant: <span className="text-parchment">{lagna}</span>
                            {lagnaDeg != null && (
                                <span className="text-slate-500"> · {lagnaDeg.toFixed(2)}°</span>
                            )}
                        </p>
                    )}
                    {chain && (
                        <p>Ruling chain: <span className="text-parchment">{chain}</span></p>
                    )}
                    {confidenceUpper && (
                        <p>Confidence: <span className="text-parchment">{confidenceUpper}</span></p>
                    )}
                    <p className="text-slate-400">
                        This birth time has been used for all your readings since {rectifiedDate}.
                    </p>
                    <p className="text-xs text-slate-500 pt-2">
                        If you feel this analysis needs review, one of our astrologers can take a deeper look.
                    </p>
                </div>
            )}
        </Card>
    );
}
